import { SPACING_L_XS } from '@audi/audi-ui-design-tokens';
import { Headline } from '@oneaudi/unified-web-components';
import { useState } from 'react';
import { VATConfig } from '~/app/market-config';
import {
	StyledDialogActionButtonWrapper,
	StyledInformationSection,
} from '~/app/pages/configurator/components/more-information-dialog/components/more-information-dialog-header/styles/more-information-dialog-header-style';
import { sliderItemWidth } from '~/app/pages/configurator/styles/configurator-tokens';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { ProductInformationText } from '~/components/product-information';
import { SlidePanel } from '~/components/slider-panel';
import { RenderHtml } from '~/components/text';
import { PriceFragment, StatusFragment } from '~/graphql/__generated__/graphql';
import { PriceOrError } from '~/graphql/mapper';
import { isDisclaimerAvailable } from '~/utils/date';
import { DialogItemActionButton } from '../dialog-item-action-button';

export function MoreInformationDialogHeader({
	headline,
	imageUrl,
	imageUrls,
	status,
	price,
	rate,
	vatConfig,
	onAddToConfiguration,
	imageFit = 'object-cover',
	hidePriceAndAvailability,
	disclaimerHeadline,
	disclaimerContent,
	disclaimerStartDate,
	disclaimerEndDate,
}: {
	headline: string;
	imageUrl: string | null | undefined;
	imageUrls: string[];
	status: StatusFragment;
	price: PriceFragment | undefined | null;
	rate: PriceOrError | undefined | null;
	vatConfig: VATConfig;
	onAddToConfiguration: (() => void) | undefined;
	/** Workaround to adapt object-fit for rims */
	imageFit?: 'object-contain' | 'object-cover';
	hidePriceAndAvailability?: boolean;
	disclaimerHeadline?: string;
	disclaimerContent?: string;
	disclaimerStartDate?: string;
	disclaimerEndDate?: string;
}) {
	return (
		<StyledInformationSection imageUrl={imageUrl}>
			{imageUrls?.length > 1 && <MoreInformationDialogSlider imageUrls={imageUrls} />}
			<div className="flex flex-col gap-6">
				<div className="flex flex-col gap-4">
					<Headline tag={'h2'} variant={{ type: 'headline', order: '3', weight: 'bold' }}>
						<RenderHtml html={headline} />
					</Headline>

					{!hidePriceAndAvailability && (
						<ProductInformationText vatConfig={vatConfig} status={status} price={price} rate={rate} />
					)}
				</div>

				{typeof onAddToConfiguration === 'function' && (
					<StyledDialogActionButtonWrapper>
						<DialogItemActionButton status={status} onAddToConfiguration={onAddToConfiguration} />
					</StyledDialogActionButtonWrapper>
				)}
				{disclaimerContent && isDisclaimerAvailable(disclaimerStartDate, disclaimerEndDate) && (
					<div className="disclaimer-wrapper">
						<Disclaimer type="item" headline={disclaimerHeadline} content={disclaimerContent} />
					</div>
				)}
			</div>

			{imageUrl && imageUrls?.length <= 1 && (
				<img src={imageUrl} alt={headline} className={`max-h-64 w-full ${imageFit}`} loading="lazy" />
			)}
		</StyledInformationSection>
	);
}

export function MoreInformationDialogSlider({ imageUrls }: { imageUrls: string[] }) {
	const [activeMoreInfo] = useState(-1);
	return (
		<div className="float-block slider" style={{ width: sliderItemWidth, margin: 'auto' }}>
			<SlidePanel
				catalogueHeadline={'test'}
				className="more-info-slider"
				sliderSpaceBetween={0}
				sliderItemWidth={sliderItemWidth}
				navigationLeftPosition={SPACING_L_XS}
				activeIndex={activeMoreInfo}>
				{imageUrls?.map((imgUrl) => (
					<img
						key={imgUrl}
						src={imgUrl}
						alt={imgUrl}
						className={`h-full max-h-64 w-full object-contain`}
						loading="lazy"
					/>
				)) ?? []}
			</SlidePanel>
		</div>
	);
}
